import { CopyOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FormSectionTitle } from '@leedpulse-monorepo/components';
import { Alert, Button, Divider, Flex, Form, Input, InputNumber, QRCode, Segmented, Select, Spin, Switch, Tooltip, Typography, message } from 'antd';
import AuthenticationContext from '../../../../contexts/auth/AuthenticationContext';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import HttpClient from '../../../../../core/HttpClient';
import { EProductType, UserRole, partnersTypeAsString } from '@leedpulse-monorepo/enums';
import { IStripeProducts } from '../../../../../domain/models/stripe/Stripe';
// @ts-expect-error: No types available for react-copy-to-clipboard
import { CopyToClipboard } from "react-copy-to-clipboard";

import styles from './PaymentForm.module.css';
import PaymentQrCode from './PaymentQrCode';
import DomainsSelect from '../../../domains_page/select/DomainsSelect';

interface PaymentFormLine {
    product?: string;
    quantity?: number;
};

interface PaymentFormValues {
    id_domain?: string;
    send_by_email?: boolean;
    email?: string;
    portability_requested?: boolean;
    reusable?: boolean;
    products_lines?: PaymentFormLine[];
};

interface PayablePlanOptions {
    all: PayablePlanOption[];
    filtered: PayablePlanOption[];
    public: PayablePlanOption[];
    private: PayablePlanOption[];
    partners: Record<string, PayablePlanOption[]>;
}

interface PayablePlanOption {
    value: string; // plan id
    label: string;
    planId: string;
    priceId: string;
    productId: string;
    active: boolean;
    partner_systems: string[];
    interval?: string; // Stripe recurring interval: month, year
    no_commitment: boolean;
};

type TPlanInterval = 'month' | 'year';
type TPlanCommitment = 'commitment' | 'no_commitment';

// Real estate, the domain of most new clients
const DEFAULT_DOMAIN_ID = 'domain-8F28730C7D';

const initialValues: PaymentFormValues = {
    id_domain: DEFAULT_DOMAIN_ID,
    send_by_email: false,
    email: '',
    portability_requested: false,
    reusable: false,
    products_lines: [
        {
            product: undefined,
            quantity: 1,
        },
    ],
};


export default function PaymentForm() {
    const auth = useContext(AuthenticationContext);

    const [form] = Form.useForm();
    const qrCodeRef = useRef<HTMLHtmlElement>(null);

    const [stripeProducts, setStripeProducts] = useState<IStripeProducts | null>(null);
    const [loadingCatalog, setLoadingCatalog] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
    const [planInterval, setPlanInterval] = useState<TPlanInterval>('month');
    const [planCommitment, setPlanCommitment] = useState<TPlanCommitment>('commitment');

    const readOnly = useMemo(() => {
        return paymentUrl || submitting ? true : false;
    }, [paymentUrl, submitting]);

    const isOwner = auth.authentication?.role === UserRole.owner;
    const reusable = Form.useWatch('reusable', form) === true;

    const payablePlanOptions: PayablePlanOptions = useMemo(() => {
        const products = stripeProducts?.products ?? [];
        const out: PayablePlanOption[] = [];

        for (const product of products) {
            if (!product.active) continue;
            for (const price of product.prices ?? []) {
                if (!price.active) continue;
                for (const plan of price.plans ?? []) {
                    if (!plan.has_valid_stripe_price) continue;
                    if (plan.archived) continue;

                    const unitAmount = price.unit_amount ?? 0;
                    const currency = (price.currency || 'eur').toUpperCase();
                    const formatted =
                        unitAmount != null
                            ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(unitAmount / 100)
                            : '';
                    const interval = price.recurring?.interval ? `/${price.recurring.interval}` : '';

                    out.push({
                        value: plan.id,
                        planId: plan.id,
                        priceId: price.id,
                        productId: product.id,
                        label: `${plan.name} - ${formatted}${interval} (${(plan.credits.leedbucks ?? 0) >= 99999 ? 'crédits illimités' : `${plan.credits.leedbucks ?? 0} crédits`})`,
                        active: plan.active,
                        partner_systems: plan.partner_systems,
                        interval: price.recurring?.interval,
                        no_commitment: plan.no_commitment === true,
                    });
                }
            }
        }

        out.sort((a, b) => a.label.localeCompare(b.label, 'fr'));

        const filtered = out.filter((o) => o.interval === planInterval && o.no_commitment === (planCommitment === 'no_commitment'));

        const publics: PayablePlanOption[] = [];
        const privates: PayablePlanOption[] = [];
        const partners = new Map<string, PayablePlanOption[]>();
        filtered.forEach((o) => {
            if (o.partner_systems.length === 0) {
                if (o.active) publics.push(o);
                else privates.push(o);
            } else {
                o.partner_systems.forEach((p) => {
                    partners.set(p, [...(partners.get(p) ?? []), o]);
                });
            }
        });

        return {
            all: out,
            filtered,
            public: publics,
            private: privates,
            partners: Object.fromEntries(partners),
        };
    }, [stripeProducts, planInterval, planCommitment]);

    const watchedLines = Form.useWatch('products_lines', form) as PaymentFormLine[] | undefined;
    const selectedPlanIds: string[] = (watchedLines ?? []).map((l) => l?.product).filter((v): v is string => Boolean(v));
    const selectedPlanIdSet = useMemo(() => new Set(selectedPlanIds), [selectedPlanIds]);

    // A plan picked under the previous filter never matches the new one
    const clearSelectedPlans = () => {
        const lines = (form.getFieldValue('products_lines') ?? []) as PaymentFormLine[];
        form.setFieldValue('products_lines', lines.map((l) => ({ ...l, product: undefined })));
    };

    const onFinish = async (values: PaymentFormValues) => {
        if (!auth.authentication?.token) {
            message.error('Vous devez être authentifié.');
            return;
        }

        setSubmitting(true);
        setPaymentUrl(null);
        try {
            const sendByEmail = values?.send_by_email === true;
            const email: string | undefined = typeof values?.email === 'string' ? values.email.trim() : undefined;
            const portabilityRequested = values?.portability_requested === true;
            const isReusable = values?.reusable === true;
            const lines: PaymentFormLine[] = Array.isArray(values?.products_lines) ? values.products_lines : [];

            const items = lines
                .filter((l) => l?.product && Number(l?.quantity) > 0)
                .map((l) => ({ plan_id: l.product, quantity: Number(l.quantity) }));

            const json = await HttpClient.post(
                `/${EProductType.LEEDFLOW}/stripe/backoffice/${isReusable ? 'create-reusable-payment-link' : 'create-payment-link'}`,
                {
                    ...(email ? { email } : {}),
                    id_domain: values.id_domain,
                    send_by_email: sendByEmail,
                    portability_requested: portabilityRequested,
                    items,
                },
                auth.authentication.token
            );

            const url = json?.data?.url;
            if (typeof url !== 'string' || url.length === 0) {
                throw new Error('URL Stripe manquante dans la réponse.');
            }

            setPaymentUrl(url);
            if (sendByEmail && email) {
                message.success(`Le mail a bien été envoyé à ${email}`);
            } else {
                message.success('Lien de paiement créé');
            }
        } catch (e: unknown) {
            console.error(e);
            const errMsg = e instanceof Error ? e.message : 'Erreur lors de la création du lien de paiement';
            message.error(errMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const downloadCanvasQRCode = () => {
        const canvas = qrCodeRef.current?.querySelector<HTMLCanvasElement>('canvas');
        if (canvas) {
            const url = canvas.toDataURL();

            const a = document.createElement('a');
            a.download = `Leedflow - Lien de paiement - ${new Date().toISOString()}.png`;
            a.href = url;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    };

    useEffect(() => {
        const run = async () => {
            if (!auth.authentication?.token) return;
            setLoadingCatalog(true);
            try {
                const params = new URLSearchParams({ product_status: 'active', visibility: 'all' });
                const json = await HttpClient.get(
                    `/${EProductType.LEEDFLOW}/stripe/backoffice/products-with-plans?${params.toString()}`,
                    auth.authentication.token
                );
                if (json?.success && json.data) {
                    setStripeProducts(json.data as IStripeProducts);
                } else {
                    setStripeProducts(null);
                }
            } catch (e) {
                console.error('[Stripe Leedflow catalog] error:', e);
                setStripeProducts(null);
            } finally {
                setLoadingCatalog(false);
            }
        };
        run();
    }, [auth.authentication?.token]);


    return (
        <Form layout="vertical" form={form} onFinish={onFinish} initialValues={initialValues}>
            <FormSectionTitle title="Nouveau lien de paiement" />
            {loadingCatalog ? (
                <Flex justify="center" align="center" style={{ padding: "1rem" }}>
                    <Spin />
                </Flex>
            ) : payablePlanOptions.all.length === 0 ? (
                <Alert
                    type="warning"
                    showIcon
                    message="Aucun plan disponible"
                    description="Aucun plan actif n'est relié à un produit Stripe actif avec un price actif."
                />
            ) : (
                <>
                    <Form.Item
                        label="Domaine"
                        name="id_domain"
                        tooltip="Le métier de chaque société créée par ce lien"
                        rules={[{ required: true, message: 'Veuillez choisir un domaine.' }]}
                    >
                        <DomainsSelect disabled={readOnly} placeholder="Choisir un domaine" />
                    </Form.Item>
                    <Flex gap="0 24px" wrap="wrap">
                        <Form.Item
                            label="Portabilité demandée"
                            name="portability_requested"
                            valuePropName="checked"
                            style={{ marginBottom: '1rem' }}
                        >
                            <Switch disabled={readOnly} />
                        </Form.Item>
                        {isOwner && (
                            <Form.Item
                                label="Lien réutilisable"
                                name="reusable"
                                valuePropName="checked"
                                tooltip="Payable plusieurs fois : chaque paiement crée une nouvelle société. Pour le supprimer, passez par Stripe."
                                style={{ marginBottom: '1rem' }}
                            >
                                <Switch disabled={readOnly} />
                            </Form.Item>
                        )}
                    </Flex>
                    <Flex gap="0 24px" wrap="wrap">
                        <Form.Item label="Facturation" style={{ marginBottom: '1rem' }}>
                            <Segmented<TPlanInterval>
                                value={planInterval}
                                onChange={(v) => {
                                    setPlanInterval(v);
                                    clearSelectedPlans();
                                }}
                                options={[
                                    { label: 'Mensuel', value: 'month' },
                                    { label: 'Annuel', value: 'year' },
                                ]}
                                disabled={readOnly}
                            />
                        </Form.Item>
                        <Form.Item label="Engagement" style={{ marginBottom: '1rem' }}>
                            <Segmented<TPlanCommitment>
                                value={planCommitment}
                                onChange={(v) => {
                                    setPlanCommitment(v);
                                    clearSelectedPlans();
                                }}
                                options={[
                                    { label: 'Avec', value: 'commitment' },
                                    { label: 'Sans', value: 'no_commitment' },
                                ]}
                                disabled={readOnly}
                            />
                        </Form.Item>
                    </Flex>
                    <Form.List
                        name="products_lines"
                        rules={[
                            {
                                validator: async (_, lines) => {
                                    if (!Array.isArray(lines) || lines.length === 0) {
                                        return Promise.reject(new Error('Veuillez ajouter au moins une ligne produit.'));
                                    }
                                    const typedLines = lines as PaymentFormLine[];
                                    const items = typedLines.filter((l) => l?.product && Number(l?.quantity) >= 1);
                                    if (items.length === 0) {
                                        return Promise.reject(new Error('Veuillez renseigner au moins une ligne valide.'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        {(fields, { add, remove }) => (
                            <>
                                <Flex className={styles.planList} vertical>
                                    {fields.map((field, index) => {
                                        const currentSelected = form.getFieldValue(['products_lines', field.name, 'product']) as string | undefined;
                                        const currentSelectedLabel =
                                            currentSelected != null
                                                ? payablePlanOptions.all.find((o) => o.value === currentSelected)?.label
                                                : undefined;
                                        return (
                                            <React.Fragment key={field.key}>
                                                {index > 0 && <Divider size="small" />}
                                                <div
                                                    className={styles.planItemRow}
                                                >
                                                    <Form.Item
                                                        label="Produit"
                                                        name={[field.name, 'product']}
                                                        rules={[{ required: true, message: 'Veuillez sélectionner un produit' }]}
                                                        style={{ margin: 0, flex: 1, minWidth: 0 }}
                                                    >
                                                        <Select
                                                            className={styles.planItemSelect}
                                                            showSearch
                                                            optionFilterProp="label"
                                                            placeholder="Sélectionner un plan"
                                                            title={currentSelectedLabel}
                                                            options={[
                                                                {
                                                                    label: "Plans publics",
                                                                    title: "publics",
                                                                    options: payablePlanOptions.public.map((o) => ({
                                                                        key: `public-${o.value}`,
                                                                        value: o.value,
                                                                        label: o.label,
                                                                        disabled: selectedPlanIdSet.has(o.value) && o.value !== currentSelected,
                                                                    })),
                                                                },
                                                                ...Object.entries(payablePlanOptions.partners).map(([key, value]) => ({
                                                                    label: `Plans ${partnersTypeAsString(key)} (${key})`,
                                                                    key: `partner-${key}`,
                                                                    options: value.map((o) => ({
                                                                        value: o.value,
                                                                        label: o.label,
                                                                        disabled: selectedPlanIdSet.has(o.value) && o.value !== currentSelected,
                                                                    })),
                                                                })),
                                                                {
                                                                    label: "Plans privés",
                                                                    title: "privés",
                                                                    options: payablePlanOptions.private.map((o) => ({
                                                                        key: `private-${o.value}`,
                                                                        value: o.value,
                                                                        label: o.label,
                                                                        disabled: selectedPlanIdSet.has(o.value) && o.value !== currentSelected,
                                                                    })),
                                                                },
                                                            ].filter((group) => group.options.length > 0)}
                                                            notFoundContent="Aucun plan ne correspond à ces filtres"
                                                            optionRender={(option) => (
                                                                <Tooltip title={option.label} placement="topLeft">
                                                                    <span className={styles.planItemLabel}>
                                                                        {option.label}
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                            disabled={readOnly}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item
                                                        label="Quantité"
                                                        name={[field.name, 'quantity']}
                                                        rules={[{ required: true, message: 'Veuillez entrer une quantité' }]}
                                                        style={{ margin: 0, flex: 3 }}
                                                    >
                                                        <InputNumber
                                                            min={1}
                                                            max={999}
                                                            precision={0}
                                                            style={{ width: '100%' }}
                                                            disabled={readOnly}
                                                        />
                                                    </Form.Item>

                                                    {(fields.length > 1 && !readOnly) && (
                                                        <Form.Item label=" " style={{ margin: 0, display: 'flex', justifyContent: 'center' }}>
                                                            <MinusCircleOutlined
                                                                onClick={() => remove(field.name)}
                                                                style={{ color: 'var(--danger-color)' }}
                                                            />
                                                        </Form.Item>
                                                    )}
                                                </div>
                                            </React.Fragment>
                                        );
                                    })}
                                </Flex>

                                {(payablePlanOptions.filtered.length > fields.length && !readOnly) && <Flex gap={8} style={{ marginTop: "1rem" }}>
                                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ quantity: 1 })} style={{ flex: 1 }}>
                                        Ajouter une ligne
                                    </Button>
                                </Flex>}
                            </>
                        )}
                    </Form.List>
                </>
            )}

            {paymentUrl ? (
                <div style={{ marginTop: "1rem" }}>
                    <FormSectionTitle title="Lien de paiement" />
                    <Flex vertical gap={10}>
                        <Alert
                            message={
                                <Typography.Text>
                                    {reusable
                                        ? 'Le lien de paiement est réutilisable : chaque paiement crée une nouvelle société. Pour le désactiver, passer par Stripe.'
                                        : 'Le lien de paiement est utilisable une seule fois'}
                                </Typography.Text>
                            }
                            type="info"
                            showIcon
                        />
                        <CopyToClipboard
                            text={paymentUrl}
                            onCopy={() => message.success('Lien de paiement copié')}
                        >
                            <Input
                                className={styles.paymentUrlInput}
                                suffix={
                                    <CopyOutlined
                                        // fix: if i don't add this the icon will not trigger the copy
                                        onClick={async (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            await navigator.clipboard.writeText(paymentUrl);
                                            message.success('Lien de paiement copié');
                                        }}
                                    />
                                }
                                value={paymentUrl ?? ''}
                                readOnly
                            />
                        </CopyToClipboard>
                        <Flex ref={qrCodeRef} justify="center" align="center">
                            <PaymentQrCode
                                status={submitting ? 'loading' : 'active'}
                                value={paymentUrl}
                            />
                        </Flex>
                    </Flex>
                </div>
            ) : null}

            <Divider />
            <Flex justify="space-between">
                {paymentUrl ? (
                    <Button loading={submitting} onClick={() => {
                        setPaymentUrl(null)
                        form.resetFields()
                    }}>
                        Nouveau lien
                    </Button>

                ) : <div />}
                {!readOnly ?
                    <Button type="primary" htmlType="submit" loading={submitting}>
                        Créer le lien
                    </Button> :
                    !submitting && <Button type="primary" onClick={downloadCanvasQRCode}>
                        Télécharger le QR Code
                    </Button>
                }
            </Flex>

            <Flex justify="center" align="center">
                <Spin spinning={submitting} />
            </Flex>
        </Form >
    );
}
